import { Link } from 'react-router';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Article = {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
  cover?: string;
};

type Props = {
  articles: ReadonlyArray<Article>;
  excludeSlug?: string;
};

const GRADIENTS: ReadonlyArray<string> = [
  'bg-[conic-gradient(from_45deg,#84CC16_0deg,#1A1A2E_120deg,#F5F3EF_240deg,#84CC16_360deg)]',
  'bg-[conic-gradient(from_90deg,#EA580C_0deg,#1A1A2E_180deg,#EA580C_360deg)]',
  'bg-[linear-gradient(135deg,#1A1A2E_0%,#84CC16_100%)]',
  'bg-[conic-gradient(from_0deg,#F5F3EF_0deg,#1A1A2E_90deg,#F5F3EF_180deg,#1A1A2E_270deg,#F5F3EF_360deg)]',
  'bg-[linear-gradient(45deg,#84CC16_0%,#A3E635_50%,#1A1A2E_100%)]',
  'bg-[conic-gradient(from_180deg,#EA580C_0deg,#F5F3EF_120deg,#1A1A2E_240deg,#EA580C_360deg)]',
  'bg-[linear-gradient(225deg,#26264A_0%,#EA580C_100%)]',
  'bg-[conic-gradient(from_270deg,#84CC16_0deg,#1A1A2E_90deg,#84CC16_180deg,#1A1A2E_270deg,#84CC16_360deg)]',
];

function formatDate(d: string) {
  try {
    return new Date(d)
      .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      .toUpperCase();
  } catch {
    return d;
  }
}

function estimateReadTime(desc: string): string {
  const words = desc.split(/\s+/).length;
  const mins = Math.max(3, Math.round(words / 30));
  return `${mins} MIN`;
}

export default function BlogArticleGridV2({ articles, excludeSlug }: Props) {
  const filtered = excludeSlug ? articles.filter((a) => a.slug !== excludeSlug) : articles;

  if (!filtered.length) {
    return (
      <section className="bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-8">
          <SectionLabel className="justify-center">Derniers articles</SectionLabel>
          <h2 className="mt-6 font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
            Le terrain parle. <span className="underline-lime">On écoute.</span>
          </h2>
          <p className="mt-6 font-marketing-body text-[16px] text-charcoal-600">
            Pas d'article dans cette catégorie pour l'instant. Reviens bientôt — un nouvel article
            chaque lundi sur deux.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-paper py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>Derniers articles</SectionLabel>
            <h2 className="mt-6 font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
              Le terrain parle.
              <br />
              <span className="underline-lime">On écoute.</span>
            </h2>
          </div>
          <div className="inline-flex items-center gap-3 border-2 border-charcoal-800 bg-white px-4 py-2 shadow-neobrutal-3">
            <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
              Trier par
            </span>
            <select className="bg-transparent font-marketing-display text-[13px] font-bold text-charcoal-800 focus:outline-none cursor-pointer">
              <option>Plus récent</option>
              <option>Plus lu</option>
              <option>Plus ancien</option>
            </select>
          </div>
        </div>

        <ul className="mt-12 grid gap-7 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((a, idx) => {
            const gradient = GRADIENTS[idx % GRADIENTS.length];
            const category = a.tags[0] ?? 'Article';
            return (
              <li key={a.slug}>
                <Link
                  to={`/blog/${a.slug}`}
                  className="group flex h-full flex-col border-2 border-charcoal-800 bg-white shadow-neobrutal-6 transition-transform hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-10"
                >
                  <div className={`relative h-44 overflow-hidden ${gradient}`}>
                    {a.cover && (
                      <img
                        src={a.cover}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                    <span className="absolute bottom-4 left-4 border-2 border-charcoal-800 bg-white px-2.5 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-800 shadow-neobrutal-2">
                      {category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col border-t-2 border-charcoal-800 p-6">
                    <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                      {formatDate(a.date)} · {estimateReadTime(a.description)}
                    </div>
                    <h3 className="mt-3 font-marketing-display text-[19px] font-bold leading-[1.2] tracking-[-0.02em] text-charcoal-800 md:text-[20px]">
                      {a.title}
                    </h3>
                    <p className="mt-3 line-clamp-3 font-marketing-body text-[14.5px] leading-relaxed text-charcoal-600">
                      {a.description}
                    </p>
                    <div className="mt-5 flex items-center justify-between border-t-2 border-dashed border-charcoal-200 pt-4 font-marketing-mono text-[10.5px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
                      <span>Par {a.author}</span>
                      <span className="flex h-7 w-7 items-center justify-center border-2 border-charcoal-800 bg-paper transition-transform group-hover:bg-lime-500">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.6"
                        >
                          <path d="M5 12h14" />
                          <path d="M13 6l6 6-6 6" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
